import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { appendCountrySuffix } from '@/utils/custom-location';
import { getMapStyleForCurrentTheme } from '@/services/map-service';
import { formatDistance, haversineMeters } from '@/utils/distance';

/**
 * Inline geocode preview for low-confidence, no-result, and discrepancy cases.
 *
 * Shown after the backend `/api/geocode` returns a non-high-confidence result OR when an
 * address edit produces coordinates more than the configured threshold away from the
 * previously-saved position.
 *
 * The dispatcher can:
 *   - Glance and accept Geocodio's best guess (default behavior — passive)
 *   - Drag the pin to correct it (status → MANUAL)
 *   - Pick from the alternate matches dropdown (status → MANUAL)
 *   - In no-result mode: drop a pin manually
 *   - In discrepancy mode: keep the previous coordinate
 *
 * Wraps a Google Maps panel for the map view; Google Maps JS SDK is already loaded
 * for the main map screen so this dependency is free.
 */

export interface GeocodePreviewAlternate {
    formatted: string;
    lat: number;
    lng: number;
    accuracyType?: string;
    accuracyScore?: number;
}

export type GeocodePreviewMode = 'low-confidence' | 'no-result' | 'discrepancy';

/** A known client/site record whose stored address matches the typed text but whose coordinate
 *  isn't trusted yet. When present (and the user has write permission), the modal offers to save
 *  a corrected pin back to that record. */
export interface GeocodePreviewWriteBackTarget {
    type: 'CLIENT' | 'SITE';
    id: number;
    name: string;
}

/** Payload passed up on Accept. Extends the original (lat, lng, formatted, alternate, components)
 *  positional call so existing orchestration keeps working, and adds the new forward-path signals. */
export interface GeocodePreviewAcceptResult {
    /** true when the dispatcher chose "Use address as typed" — resolve with the typed text and
     *  best-available coordinate, and land the record as LOW (Address Review), not MANUAL. */
    useAsTyped?: boolean;
    /** true when the write-back checkbox was shown and checked — save the pin to the client/site. */
    writeBack?: boolean;
}

export interface GeocodePreviewProps {
    open: boolean;
    mode: GeocodePreviewMode;
    formatted?: string;
    lat?: number;
    lng?: number;
    accuracyType?: string;
    alternates?: GeocodePreviewAlternate[];
    /** 2-letter country code of the geocoded result, used to pick the distance unit (US → mi, else km) */
    country?: string;
    /** The original typed address text. Prefills the editable re-search field and is the exact
     *  text returned by "Use address as typed". */
    query?: string;
    previousLat?: number;     // for discrepancy mode
    previousLng?: number;
    fallbackCenterLat?: number; // for no-result mode (e.g., company HQ)
    fallbackCenterLng?: number;
    /** require explicit confirmation before save (custom location, tour stop setup); call card is passive */
    requireConfirm?: boolean;
    /** Known client/site the typed address belongs to, offered for write-back on a corrected pin. */
    writeBackTarget?: GeocodePreviewWriteBackTarget;
    /** Whether the write-back offer may show (computed by the orchestrator; false in discrepancy
     *  mode). The checkbox additionally requires a writeBackTarget and a corrected pin. */
    canWriteBack?: boolean;
    theme?: string;

    onAccept: (lat: number, lng: number, formatted: string, alternate?: GeocodePreviewAlternate, components?: any, extra?: GeocodePreviewAcceptResult) => void;
    onRevert?: () => void;     // discrepancy mode: keep previous coords
    onCancel: () => void;
}

// Touch devices get different treatment throughout: cooperative map gestures (one finger
// scrolls the modal, two fingers pan the map), a shorter map so the address and action
// buttons fit on screen, and larger tap targets on the small link buttons. Evaluated once —
// pointer capability doesn't change mid-session.
const IS_COARSE_POINTER = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches;

// Map sizing: bigger on desktop so zoom controls + drag are comfortable; shorter on touch
// devices so the address row and pinned action bar leave the map enough breathing room
// without forcing a scroll just to read the resolved address.
const MAP_HEIGHT_CSS = IS_COARSE_POINTER ? 'min(45vh, 360px)' : 'min(70vh, 520px)';
const MAP_MIN_HEIGHT_PX = IS_COARSE_POINTER ? 220 : 280;

// Touch-target padding for the small link buttons ("Use coordinates instead",
// "Compare with previous location"). Vertical-only so the text stays flush with the
// left edge of its column; ~44px effective height on touch, compact on desktop.
const LINK_TAP_PADDING = IS_COARSE_POINTER ? '10px 0' : '0';

function isGoogleLoaded(): boolean {
    return typeof (window as any).google !== 'undefined'
        && (window as any).google?.maps?.Map != null;
}

export function GeocodePreview(props: GeocodePreviewProps) {
    const {
        open,
        mode,
        formatted: initialFormatted,
        lat: initialLat,
        lng: initialLng,
        accuracyType,
        alternates: initialAlternates = [],
        country,
        query,
        previousLat,
        previousLng,
        fallbackCenterLat,
        fallbackCenterLng,
        requireConfirm = false,
        writeBackTarget,
        canWriteBack = false,
        theme,
        onAccept,
        onRevert,
        onCancel,
    } = props;

    const mapDivRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<any>(null);
    const markerRef = useRef<any>(null);
    const previousMarkerRef = useRef<any>(null);
    const lineRef = useRef<any>(null);
    // Monotonic id for in-flight reverse-geocode requests. Rapid drags can fire multiple
    // dragend events; if an earlier response lands after a later one, it would otherwise
    // overwrite the displayed address with stale text. We only apply a response whose id
    // matches the most-recent request.
    const reverseGeocodeRequestIdRef = useRef<number>(0);

    // Working state — what the user has chosen so far (may differ from props if they dragged)
    const [currentLat, setCurrentLat] = useState<number | undefined>(initialLat);
    const [currentLng, setCurrentLng] = useState<number | undefined>(initialLng);
    const [currentFormatted, setCurrentFormatted] = useState<string | undefined>(initialFormatted);
    const [selectedAlternate, setSelectedAlternate] = useState<GeocodePreviewAlternate | undefined>(undefined);
    const [resolving, setResolving] = useState<boolean>(false);

    // Re-search state. The alternates list and the effective mode can both change when the
    // dispatcher edits the address and searches again (a no-result search may find matches, or a
    // low-confidence search may come back empty). Discrepancy mode never re-searches — it keeps
    // its props verbatim.
    const [alternates, setAlternates] = useState<GeocodePreviewAlternate[]>(initialAlternates);
    const [currentMode, setCurrentMode] = useState<GeocodePreviewMode>(mode);
    const [searchText, setSearchText] = useState<string>(query ?? '');
    const [searching, setSearching] = useState<boolean>(false);
    // Whether the write-back checkbox is ticked. Only meaningful when a writeBackTarget exists,
    // the user has permission, and they resolve via a corrected pin (drag / alternate).
    const [writeBackChecked, setWriteBackChecked] = useState<boolean>(false);

    // Monotonic id for in-flight forward-geocode (re-search) requests, mirroring the reverse
    // geocode guard: a stale response from an earlier search must not overwrite a later one.
    const forwardGeocodeRequestIdRef = useRef<number>(0);

    // The map's click-to-drop handler is registered once at map init but must react to the mode
    // in effect at click time (a re-search can flip low-confidence ↔ no-result after init).
    // A ref keeps the latest mode readable from that stable listener closure.
    const currentModeRef = useRef<GeocodePreviewMode>(mode);
    useEffect(() => { currentModeRef.current = currentMode; }, [currentMode]);

    // Reverse-geocode capture: when the user drops/drags a pin, the backend resolves it to
    // a street address + parsed components. We keep both around so:
    //   1. The "Use coordinates instead" toggle can flip the displayed text back and forth
    //      between the resolved address and the raw lat/lng without losing the address text.
    //   2. On Accept (no-result mode), we can pass the components downstream so the saved
    //      record gets a proper city/state/zip/country instead of empty fields.
    const [reverseGeocodedAddress, setReverseGeocodedAddress] = useState<string | undefined>(undefined);
    const [reverseGeocodedComponents, setReverseGeocodedComponents] = useState<any | undefined>(undefined);
    const [useCoordsForFormatted, setUseCoordsForFormatted] = useState<boolean>(false);

    // Discrepancy mode shows a warning banner with an opt-in toggle to overlay the
    // previously-saved pin on the map. Default is off so the dispatcher keeps the
    // normal preview tools (drag, alternates) without the zoomed-out fitBounds view.
    const [showPreviousOnMap, setShowPreviousOnMap] = useState<boolean>(false);

    // Browser geolocation for no-result mode map centering — gives the dispatcher a useful
    // starting view (their own area) instead of a blank atlas. Only requested in no-result mode
    // and only when caller didn't supply a fallback. Resolves to undefined on denial / error /
    // unavailability, in which case the map falls back to the continental US default.
    const [geoCenterLat, setGeoCenterLat] = useState<number | undefined>(undefined);
    const [geoCenterLng, setGeoCenterLng] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (!open) return;
        if (mode !== 'no-result') return;
        if (currentLat != null || fallbackCenterLat != null) return;
        if (typeof navigator === 'undefined' || !navigator.geolocation) return;
        let cancelled = false;
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                if (cancelled) return;
                setGeoCenterLat(pos.coords.latitude);
                setGeoCenterLng(pos.coords.longitude);
            },
            () => { /* user denied or geolocation unavailable; default center is used */ },
            { timeout: 5000, maximumAge: 60_000 * 10 }   // accept a 10-minute-old cached fix
        );
        return () => { cancelled = true; };
    }, [open, mode, currentLat, fallbackCenterLat]);

    /**
     * When the user drags the pin or clicks-to-drop in no-result mode, the formatted address
     * shown above the map gets stale ("Main St, Forney TX" with a pin in downtown Dallas, for
     * example). Reverse-geocode the new coordinates via the same backend endpoint so the
     * displayed address always matches the pin position. On failure (network, no-match), fall
     * back to displaying the bare "lat, lng" string so the user at least sees the coords moved.
     */
    const reverseGeocodeToUpdateAddress = useCallback((lat: number, lng: number) => {
        const requestId = ++reverseGeocodeRequestIdRef.current;
        setResolving(true);
        const isLatest = () => requestId === reverseGeocodeRequestIdRef.current;
        try {
            const api: any = (window as any).api;
            if (api?.geocodeViaProxy) {
                api.geocodeViaProxy(
                    { query: `${lat},${lng}` },
                    (result: any) => {
                        if (!isLatest()) return;
                        if (result?.formatted) {
                            // Match the suffix convention applied at the proxy-shape translation
                            // boundary so the modal's displayed address stays consistent with the
                            // value that ultimately gets saved.
                            const resolved = appendCountrySuffix(result.formatted, result?.components?.country);
                            setReverseGeocodedAddress(resolved);
                            setReverseGeocodedComponents(result?.components ?? undefined);
                            setCurrentFormatted(useCoordsForFormatted
                                ? `${lat.toFixed(6)}, ${lng.toFixed(6)}`
                                : resolved);
                        } else {
                            setReverseGeocodedAddress(undefined);
                            setReverseGeocodedComponents(undefined);
                            setCurrentFormatted(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
                        }
                        setResolving(false);
                    },
                    () => {
                        if (!isLatest()) return;
                        setCurrentFormatted(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
                        setResolving(false);
                    }
                );
                return;
            }
        } catch { /* ignore */ }
        setCurrentFormatted(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        setResolving(false);
    }, []);

    // Move (or create) the draggable primary marker to a position and re-center the map. Used by
    // the re-search flow, which can introduce a pin where there wasn't one (no-result → match).
    // The dragend handler mirrors the one attached at map init so a dragged re-search pin behaves
    // identically to a dragged initial pin.
    const placeMarkerAt = useCallback((lat: number, lng: number) => {
        if (!mapRef.current || !isGoogleLoaded()) return;
        const google = (window as any).google;
        const pos = { lat, lng };
        if (markerRef.current) {
            markerRef.current.setPosition(pos);
        } else {
            markerRef.current = new google.maps.Marker({
                position: pos,
                map: mapRef.current,
                draggable: true,
                title: 'Drag to adjust location',
            });
            markerRef.current.addListener('dragend', (e: any) => {
                const dLat = e.latLng.lat();
                const dLng = e.latLng.lng();
                setCurrentLat(dLat);
                setCurrentLng(dLng);
                setSelectedAlternate(undefined);
                reverseGeocodeToUpdateAddress(dLat, dLng);
            });
        }
        mapRef.current.panTo(pos);
        mapRef.current.setZoom(17);
    }, [reverseGeocodeToUpdateAddress]);

    /**
     * Editable-address re-search. Runs the same backend /api/geocode the orchestrator uses, then
     * swaps the modal's result state in place: new top result (pin, address, alternates), or a
     * switch to no-result when nothing came back. Resets any dragged/selected state so the search
     * result is what gets accepted unless the user adjusts again. Discrepancy mode never calls this.
     */
    const handleReSearch = useCallback(() => {
        const q = searchText.trim();
        if (!q) return;
        const api: any = (window as any).api;
        if (!api?.geocodeViaProxy) return;

        const requestId = ++forwardGeocodeRequestIdRef.current;
        const isLatest = () => requestId === forwardGeocodeRequestIdRef.current;
        setSearching(true);
        // Clear prior drag/alternate/coords-toggle state so the new search result stands alone.
        setSelectedAlternate(undefined);
        setReverseGeocodedAddress(undefined);
        setReverseGeocodedComponents(undefined);
        setUseCoordsForFormatted(false);
        setWriteBackChecked(false);

        api.geocodeViaProxy(
            { query: q },
            (result: any) => {
                if (!isLatest()) return;
                setSearching(false);
                const confidence = result?.confidence != null ? String(result.confidence).toLowerCase() : null;
                const hasHit = result && confidence !== 'none' && result.lat != null && result.lng != null;
                if (!hasHit) {
                    // Nothing usable — switch to the drop-a-pin experience, clearing any old pin.
                    setCurrentMode('no-result');
                    setAlternates([]);
                    setCurrentLat(undefined);
                    setCurrentLng(undefined);
                    setCurrentFormatted(undefined);
                    try { markerRef.current?.setMap(null); } catch { /* ignore */ }
                    markerRef.current = null;
                    return;
                }
                const lat = Number(result.lat);
                const lng = Number(result.lng);
                const resolvedFormatted = appendCountrySuffix(result.formatted ?? '', result?.components?.country);
                const nextAlternates: GeocodePreviewAlternate[] = Array.isArray(result.alternates)
                    ? result.alternates
                        .filter((a: any) => a?.lat != null && a?.lng != null)
                        .map((a: any) => ({
                            formatted: appendCountrySuffix(a.formatted ?? '', a?.components?.country ?? result?.components?.country),
                            lat: Number(a.lat),
                            lng: Number(a.lng),
                            accuracyType: a?.accuracyType ?? undefined,
                        }))
                    : [];
                setCurrentMode('low-confidence');
                setCurrentLat(lat);
                setCurrentLng(lng);
                setCurrentFormatted(resolvedFormatted);
                setReverseGeocodedComponents(result?.components ?? undefined);
                setAlternates(nextAlternates);
                placeMarkerAt(lat, lng);
            },
            () => {
                if (!isLatest()) return;
                setSearching(false);
                // Leave existing state as-is on a transport error; the dispatcher can retry or
                // fall back to "Use address as typed".
            }
        );
    }, [searchText, placeMarkerAt]);

    // Resolve immediately with the address exactly as the dispatcher typed it. Best-available
    // coordinate: the current pin if one exists, otherwise null (the orchestrator falls back).
    // Lands the record as LOW so it surfaces in Address Review, not MANUAL.
    const handleUseAsTyped = useCallback(() => {
        onAccept(
            currentLat as any,
            currentLng as any,
            (query ?? '').trim(),
            undefined,
            undefined,
            { useAsTyped: true },
        );
    }, [currentLat, currentLng, query, onAccept]);

    // Initialize map after modal opens & DOM is mounted
    useEffect(() => {
        if (!open || !mapDivRef.current || !isGoogleLoaded()) return;

        const google = (window as any).google;
        // Continental US center used as a last-resort default for no-result mode when neither
        // the caller's fallback nor the user's browser geolocation gave us a better anchor.
        const DEFAULT_CENTER_LAT = 39.8283;
        const DEFAULT_CENTER_LNG = -98.5795;
        const startLat = currentLat ?? fallbackCenterLat ?? geoCenterLat ?? DEFAULT_CENTER_LAT;
        const startLng = currentLng ?? fallbackCenterLng ?? geoCenterLng ?? DEFAULT_CENTER_LNG;

        const map = new google.maps.Map(mapDivRef.current, {
            zoom: currentLat != null ? 17 : 13,
            center: { lat: startLat, lng: startLng },
            // Show zoom in/out controls; hide the controls we don't need so the bar is uncluttered.
            zoomControl: true,
            zoomControlOptions: {
                position: google.maps.ControlPosition.RIGHT_CENTER,
            },
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            // Touch: cooperative so a one-finger swipe scrolls the modal body instead of being
            // swallowed by map panning (Google shows its "Use two fingers to move the map"
            // overlay). Desktop: greedy so mouse scroll-zoom works without needing Ctrl.
            gestureHandling: IS_COARSE_POINTER ? 'cooperative' : 'greedy',
            // Match the app theme at mount; the dialog remounts when reopened, so no
            // live re-styling on theme change is needed.
            styles: getMapStyleForCurrentTheme(),
        });
        mapRef.current = map;

        // Primary marker (the geocode result or the dropped pin)
        if (currentLat != null && currentLng != null) {
            markerRef.current = new google.maps.Marker({
                position: { lat: currentLat, lng: currentLng },
                map,
                draggable: true,
                title: 'Drag to adjust location',
            });
            markerRef.current.addListener('dragend', (e: any) => {
                const newLat = e.latLng.lat();
                const newLng = e.latLng.lng();
                setCurrentLat(newLat);
                setCurrentLng(newLng);
                setSelectedAlternate(undefined);
                reverseGeocodeToUpdateAddress(newLat, newLng);
            });
        }

        // Clicking the map drops a pin — but only while in no-result mode. The mode is read from
        // a ref so a re-search that switches into no-result after init still enables drop-to-pin.
        {
            map.addListener('click', (e: any) => {
                if (currentModeRef.current !== 'no-result') return;
                const newLat = e.latLng.lat();
                const newLng = e.latLng.lng();
                if (markerRef.current) {
                    markerRef.current.setPosition(e.latLng);
                } else {
                    markerRef.current = new google.maps.Marker({
                        position: e.latLng,
                        map,
                        draggable: true,
                        title: 'Drag to adjust location',
                    });
                    markerRef.current.addListener('dragend', (ev: any) => {
                        const dragLat = ev.latLng.lat();
                        const dragLng = ev.latLng.lng();
                        setCurrentLat(dragLat);
                        setCurrentLng(dragLng);
                        reverseGeocodeToUpdateAddress(dragLat, dragLng);
                    });
                }
                setCurrentLat(newLat);
                setCurrentLng(newLng);
                reverseGeocodeToUpdateAddress(newLat, newLng);
            });
        }

        // Discrepancy mode previous-pin overlay is now opt-in via the banner toggle —
        // see the dedicated effect that watches `showPreviousOnMap` below. Map init no
        // longer auto-fits both pins so the dispatcher gets the standard zoom-17 view
        // on the new location and keeps full control of drag / alternates.

        return () => {
            try {
                markerRef.current?.setMap(null);
                previousMarkerRef.current?.setMap(null);
                lineRef.current?.setMap(null);
            } catch { /* ignore */ }
            mapRef.current = null;
            markerRef.current = null;
            previousMarkerRef.current = null;
            lineRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, geoCenterLat, geoCenterLng]);

    // Reflect drag updates back onto the line in discrepancy mode (only when the
    // overlay is visible — otherwise lineRef.current is null and we no-op).
    useEffect(() => {
        if (mode === 'discrepancy' && lineRef.current && previousLat != null && previousLng != null && currentLat != null && currentLng != null) {
            lineRef.current.setPath([
                { lat: previousLat, lng: previousLng },
                { lat: currentLat, lng: currentLng },
            ]);
        }
    }, [currentLat, currentLng, mode, previousLat, previousLng]);

    // Discrepancy overlay: show / hide the previous-location pin + connecting line.
    // Showing fits bounds across both pins; hiding restores the single-pin zoom-17 view.
    useEffect(() => {
        if (mode !== 'discrepancy') return;
        if (!mapRef.current || !isGoogleLoaded()) return;
        const google = (window as any).google;
        const map = mapRef.current;

        if (showPreviousOnMap && previousLat != null && previousLng != null) {
            if (!previousMarkerRef.current) {
                previousMarkerRef.current = new google.maps.Marker({
                    position: { lat: previousLat, lng: previousLng },
                    map,
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 7,
                        fillColor: '#888888',
                        fillOpacity: 0.9,
                        strokeColor: '#444444',
                        strokeWeight: 1,
                    },
                    title: 'Previous location',
                });
            }
            if (!lineRef.current && currentLat != null && currentLng != null) {
                lineRef.current = new google.maps.Polyline({
                    path: [
                        { lat: previousLat, lng: previousLng },
                        { lat: currentLat, lng: currentLng },
                    ],
                    map,
                    strokeColor: '#999999',
                    strokeOpacity: 0.6,
                    strokeWeight: 2,
                });
            }
            if (currentLat != null && currentLng != null) {
                const bounds = new google.maps.LatLngBounds();
                bounds.extend({ lat: previousLat, lng: previousLng });
                bounds.extend({ lat: currentLat, lng: currentLng });
                map.fitBounds(bounds, 60);
            }
        } else {
            previousMarkerRef.current?.setMap(null);
            previousMarkerRef.current = null;
            lineRef.current?.setMap(null);
            lineRef.current = null;
            if (currentLat != null && currentLng != null) {
                map.panTo({ lat: currentLat, lng: currentLng });
                map.setZoom(17);
            }
        }
    }, [showPreviousOnMap, mode, previousLat, previousLng, currentLat, currentLng]);

    const handleAlternateClick = useCallback((alt: GeocodePreviewAlternate) => {
        setSelectedAlternate(alt);
        setCurrentLat(alt.lat);
        setCurrentLng(alt.lng);
        setCurrentFormatted(alt.formatted);
        // Alternate is an explicit forward-geocoded choice — drop any reverse-geocode
        // state from a previous drag so the toggle hides and the saved record uses the
        // alternate's address (not the stale drag-resolved address or its components).
        setReverseGeocodedAddress(undefined);
        setReverseGeocodedComponents(undefined);
        setUseCoordsForFormatted(false);
        if (markerRef.current) {
            markerRef.current.setPosition({ lat: alt.lat, lng: alt.lng });
        }
        if (mapRef.current) {
            mapRef.current.panTo({ lat: alt.lat, lng: alt.lng });
        }
    }, []);

    // Write-back is offered for ANY resolution with a pin — including a passive Accept of the
    // top guess. Accepting IS the human verifying the pin (the modal asks exactly that), and
    // without the offer a plain Accept leaves the record untrusted, so the modal returns on
    // every future call to a known location.
    const showWriteBack = !!writeBackTarget
        && canWriteBack
        && currentMode !== 'discrepancy'
        && currentLat != null && currentLng != null;

    const handleAccept = useCallback(() => {
        if (currentLat == null || currentLng == null) return;
        // Pass reverse-geocoded components only when the dispatcher is using the address text.
        // When they explicitly toggled to "use coordinates", they're asserting the position is
        // what matters; the components from a possibly-misleading reverse-geocode are dropped.
        const componentsToSend = useCoordsForFormatted ? undefined : reverseGeocodedComponents;
        onAccept(currentLat, currentLng, currentFormatted ?? '', selectedAlternate, componentsToSend, {
            writeBack: showWriteBack && writeBackChecked,
        });
    }, [currentLat, currentLng, currentFormatted, selectedAlternate, useCoordsForFormatted, reverseGeocodedComponents, onAccept, showWriteBack, writeBackChecked]);

    // Flip the displayed text between the reverse-geocoded address and the raw lat/lng.
    // Only meaningful when both currentLat/Lng and a reverse-geocoded address exist.
    const handleToggleCoordsText = useCallback(() => {
        setUseCoordsForFormatted((prev) => {
            const next = !prev;
            if (currentLat != null && currentLng != null) {
                setCurrentFormatted(next
                    ? `${currentLat.toFixed(6)}, ${currentLng.toFixed(6)}`
                    : (reverseGeocodedAddress ?? `${currentLat.toFixed(6)}, ${currentLng.toFixed(6)}`));
            }
            return next;
        });
    }, [currentLat, currentLng, reverseGeocodedAddress]);

    const canAccept = currentLat != null && currentLng != null;

    let title: string;
    let helperText: string;
    if (currentMode === 'discrepancy') {
        title = 'Verify Address Change';
        helperText = 'Verify the pin is correct, drag it to adjust, or choose an alternate match. You can keep the previously saved location if the new address is wrong.';
    } else if (currentMode === 'no-result') {
        title = 'Could Not Match Address';
        helperText = 'No matching address was found. Edit the address and search again, or drop a pin on the map at the correct location and accept.';
    } else {
        title = 'Verify Address';
        helperText = 'The address could not be matched precisely. Edit the address and search again, verify the pin, drag it to adjust, or choose an alternate match.';
    }

    const discrepancyDistanceMeters = useMemo(() => {
        if (currentMode !== 'discrepancy') return null;
        if (previousLat == null || previousLng == null) return null;
        if (currentLat == null || currentLng == null) return null;
        return haversineMeters(previousLat, previousLng, currentLat, currentLng);
    }, [currentMode, previousLat, previousLng, currentLat, currentLng]);

    const primaryButtonLabel = requireConfirm || currentMode !== 'low-confidence' ? 'Use This Location' : 'Accept';

    // "Use address as typed" and the editable re-search field are the dispatcher-under-pressure
    // valves — only in the forward-facing modes (never discrepancy, which is about a saved coord).
    const showTypedValve = currentMode === 'low-confidence' || currentMode === 'no-result';

    // Pinned footer (outside the scrollable body) so the actions stay visible and tappable
    // even when the map + alternates overflow a phone-sized viewport.
    const actionBar = (
        <>
            {showWriteBack ? (
                // Lives in the PINNED footer, next to Accept — the offer that stops this modal
                // from returning for this location must be visible at the moment of accepting,
                // not below the map where the pinned action bar lets users accept without ever
                // scrolling past it. marginRight:auto left-anchors it in the flex-end bar.
                <label style={{
                    display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
                    marginRight: 'auto', flex: '1 1 auto', minWidth: 0, textAlign: 'left',
                }}>
                    <input
                        type="checkbox"
                        checked={writeBackChecked}
                        onChange={(e) => setWriteBackChecked(e.target.checked)}
                        style={IS_COARSE_POINTER ? { width: '20px', height: '20px', flex: '0 0 auto' } : { flex: '0 0 auto' }}
                    />
                    <span style={{ minWidth: 0 }}>Save this location for {writeBackTarget!.name}</span>
                </label>
            ) : null}
            <button
                type="button"
                className="kstc-btn kstc-btn--secondary"
                onClick={onCancel}
            >
                Cancel
            </button>
            {currentMode === 'discrepancy' && onRevert ? (
                <button
                    type="button"
                    className="kstc-btn kstc-btn--secondary"
                    onClick={onRevert}
                >
                    Keep Previous Location
                </button>
            ) : null}
            {showTypedValve ? (
                <button
                    type="button"
                    className="kstc-btn kstc-btn--secondary"
                    onClick={handleUseAsTyped}
                >
                    Use address as typed
                </button>
            ) : null}
            <button
                type="button"
                className="kstc-btn kstc-btn--primary"
                onClick={handleAccept}
                disabled={!canAccept}
            >
                {primaryButtonLabel}
            </button>
        </>
    );

    return (
        <BaseFormModal
            open={open}
            title={title}
            theme={theme}
            showSave={false}
            showCancel={false}
            cancelText="Cancel"
            onCancel={onCancel}
            hint={helperText}
            dialogClass="kstc-dialog--large"
            footer={actionBar}
        >
            {currentMode === 'discrepancy' && discrepancyDistanceMeters != null ? (
                <div
                    role="alert"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 12px',
                        marginBottom: '10px',
                        backgroundColor: '#fff8e1',
                        border: '1px solid #f0c36d',
                        borderRadius: '4px',
                        color: '#664500',
                    }}
                >
                    <span style={{ fontSize: '1.1em' }}>⚠</span>
                    <span style={{ flex: 1 }}>
                        This address is <strong>{formatDistance(discrepancyDistanceMeters, country)}</strong> from the previously saved location.
                    </span>
                    <button
                        type="button"
                        className="kstc-btn kstc-btn--link kstc-btn--sm"
                        style={{ padding: LINK_TAP_PADDING }}
                        onClick={() => setShowPreviousOnMap((prev) => !prev)}
                    >
                        {showPreviousOnMap ? 'Hide previous location' : 'Compare with previous location'}
                    </button>
                </div>
            ) : null}

            {showTypedValve ? (
                <div style={{ marginBottom: '12px' }}>
                    <label className="kstc-form-label" htmlFor="geocode-preview-address" style={{ display: 'block', marginBottom: '4px' }}>
                        Address
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                        <input
                            id="geocode-preview-address"
                            type="text"
                            className="kstc-input"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleReSearch(); } }}
                            style={{ flex: '1 1 auto', minWidth: 0, padding: IS_COARSE_POINTER ? '10px 12px' : undefined }}
                        />
                        <button
                            type="button"
                            className="kstc-btn kstc-btn--secondary"
                            onClick={handleReSearch}
                            disabled={searching || searchText.trim().length === 0}
                            style={{ flex: '0 0 auto' }}
                        >
                            {searching ? 'Searching…' : 'Search'}
                        </button>
                    </div>
                </div>
            ) : null}

            <div
                ref={mapDivRef}
                style={{
                    width: '100%',
                    height: MAP_HEIGHT_CSS,
                    minHeight: `${MAP_MIN_HEIGHT_PX}px`,
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginBottom: '12px',
                }}
            />

            {currentFormatted || resolving ? (
                <div style={{ marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                        <span className="kstc-form-label" style={{ flex: '0 0 auto' }}>Address:</span>
                        <span style={{ opacity: resolving ? 0.6 : 1, wordBreak: 'break-word', flex: '1 1 auto', minWidth: 0 }}>
                            {resolving ? 'Resolving…' : currentFormatted}
                        </span>
                    </div>
                    {!resolving && reverseGeocodedAddress && currentLat != null && currentLng != null ? (
                        <button
                            type="button"
                            className="kstc-btn kstc-btn--link kstc-btn--sm"
                            style={{ padding: LINK_TAP_PADDING, marginTop: '2px', display: 'block', textAlign: 'left' }}
                            onClick={handleToggleCoordsText}
                        >
                            {useCoordsForFormatted ? 'Use address instead' : 'Use coordinates instead'}
                        </button>
                    ) : null}
                </div>
            ) : null}

            {alternates.length > 0 ? (
                <div style={{ marginBottom: '12px' }}>
                    <div className="kstc-form-label" style={{ marginBottom: '4px' }}>Alternate matches:</div>
                    <ul className="list-group">
                        {alternates.map((alt, i) => (
                            <li key={i} className="list-group-item" style={{ padding: '6px 10px' }}>
                                <button
                                    type="button"
                                    className="btn btn-link"
                                    style={{ width: '100%', textAlign: 'left', padding: IS_COARSE_POINTER ? '8px 0' : 0 }}
                                    onClick={() => handleAlternateClick(alt)}
                                >
                                    {alt.formatted}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {showTypedValve ? (
                <div style={{ marginBottom: '4px', opacity: 0.75, fontSize: '0.9em' }}>
                    The map pin may be approximate. It can be adjusted later in Address Review.
                </div>
            ) : null}
        </BaseFormModal>
    );
}

(window as any).GeocodePreviewReact = GeocodePreview;
